<template>
  <f7-page>
    <AppLayout>
      <template #main>
        <div v-if="group" class="group-hero">
          <!-- Banner -->
          <div class="hero-banner">
            <img
              v-if="group.banner"
              :src="group.banner"
              class="hero-banner-img"
              :alt="$t('group.bannerAlt')"
            />
            <div v-else class="hero-banner-fallback">
              <span class="hero-fallback-icon material-symbols-outlined">photo</span>
            </div>
            <div class="hero-banner-overlay"></div>

            <!-- Verified badge -->
            <div class="hero-badge-wrap">
              <span class="hero-verified-badge">
                <span class="hero-verified-icon material-symbols-outlined">verified</span>
                {{ $t('group.verifiedGroup') }}
              </span>
            </div>

            <!-- Edit banner -->
            <f7-link
              v-if="canManage"
              type="button"
              class="hero-edit-banner"
              @click="bannerInput?.click()"
            >
              <f7-icon f7="pencil" size="13" />
            </f7-link>
            <input
              ref="bannerInput"
              type="file"
              accept="image/*"
              class="hidden-file-input"
              @change="onBannerPick"
            />
          </div>

          <!-- Identity -->
          <div class="hero-identity">
            <div class="hero-identity-row">
              <div class="hero-identity-main">
                <div class="hero-logo-wrap">
                  <img
                    v-if="group.logo"
                    :src="group.logo"
                    class="hero-logo"
                    :alt="$t('group.logoAlt')"
                  />
                  <div v-else class="hero-logo-initial">{{ initial }}</div>
                  <f7-link
                    v-if="canManage"
                    class="hero-edit-logo"
                    @click="logoInput?.click()"
                  >
                    <span class="hero-edit-logo-icon material-symbols-outlined">photo_camera</span>
                  </f7-link>
                  <input
                    ref="logoInput"
                    type="file"
                    accept="image/*"
                    class="hidden-file-input"
                    @change="onLogoPick"
                  />
                </div>
                <div class="hero-info">
                  <h1 class="hero-name">{{ group.name }}</h1>
                  <p class="hero-category">
                    {{ $t(groupCategoryLabel(group.category)) }}
                    <template v-if="membersStore.members.length"> · {{ membersStore.members.length }} {{ $t('common.members') }}</template>
                  </p>
                  <div class="hero-tags">
                    <span
                      v-for="t in group.types?.slice(0, 3)"
                      :key="t"
                      class="hero-type-chip"
                    >{{ $t(groupTypeLabel(t)) }}</span>
                    <div v-if="hasGrowthBadges" class="hero-growth">
                      <span
                        v-if="healthBadge"
                        class="hero-pill"
                        :style="{ background: healthBadge.bg, color: healthBadge.color }"
                      >
                        <span class="hero-pill-icon material-symbols-outlined">monitor_heart</span> {{ $t('group.healthBadge') }} {{ health?.score ?? 0 }}%
                      </span>
                      <span
                        v-if="trustBadge"
                        class="hero-pill"
                        :style="{ background: trustBadge.bg, color: trustBadge.color }"
                      >
                        <span class="hero-pill-icon material-symbols-outlined">{{ trustBadge.icon === 'checkmark_shield_fill' ? 'verified_user' : 'lock' }}</span> {{ $t(trustBadge.label) }}
                      </span>
                      <span
                        v-if="achievementCount > 0"
                        class="hero-pill hero-pill--tertiary"
                      >
                        <span class="hero-pill-icon material-symbols-outlined">military_tech</span> {{ achievementCount }} {{ $t('common.badges') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <f7-link
                v-if="canManage"
                class="hero-invite"
                :href="`/groups/${groupId}/members/import`"
              >
                <span class="hero-invite-icon material-symbols-outlined">person_add</span>
                {{ $t('group.invite') }}
              </f7-link>
            </div>
          </div>
        </div>

        <GroupSetupChecklist
          v-if="!loading"
          class="setup-cl"
          :group-id="groupId"
          :group="group"
          :can-manage="canManage"
          :members-count="membersStore.members.length"
          :campaign-count="allCampaigns.length"
          @banner="bannerInput?.click()"
          @logo="logoInput?.click()"
          @marketplace="enableMarketplace"
          @recipient="goRecipientSetup"
          @publish="goPublish"
        />

        <!-- At a Glance Operational Ribbon -->
        <div v-if="!loading" class="glance-section">
          <div class="glance-grid grid grid-cols-3">
            <f7-link class="glance-card" :href="`/groups/${groupId}/campaigns`">
              <div class="glance-card-inner">
                <div class="glance-card-top">
                  <span class="glance-icon glance-icon--primary material-symbols-outlined">savings</span>
                  <span
                    v-if="allCampaigns.length"
                    class="glance-badge glance-badge--primary"
                  >{{ allCampaigns.length }}</span>
                </div>
                <div class="glance-card-body">
                  <div class="glance-card-value">{{ $t('grouphome.collectionsTitle') }}</div>
                  <div class="glance-card-label">{{ $t('grouphome.collections') }}</div>
                </div>
              </div>
            </f7-link>
            <f7-link class="glance-card" :href="`/groups/${groupId}/members`">
              <div class="glance-card-inner">
                <div class="glance-card-top">
                  <span class="glance-icon glance-icon--secondary material-symbols-outlined">groups</span>
                  <span class="glance-badge glance-badge--secondary">{{ membersStore.members.length }}</span>
                </div>
                <div class="glance-card-body">
                  <div class="glance-card-value">{{ $t('common.members') }}</div>
                  <div class="glance-card-label">{{ $t('grouphome.membersActive') }}</div>
                </div>
              </div>
            </f7-link>
            <f7-link class="glance-card" :href="`/groups/${groupId}/growth`">
              <div class="glance-card-inner">
                <div class="glance-card-top">
                  <span class="glance-icon glance-icon--tertiary material-symbols-outlined">monitor_heart</span>
                  <span class="glance-dot"></span>
                </div>
                <div class="glance-card-body">
                  <div class="glance-card-value">{{ health?.score ?? '—' }}%</div>
                  <div class="glance-card-label">{{ $t('grouphome.healthScore') }}</div>
                </div>
              </div>
            </f7-link>
          </div>
        </div>

        <f7-block-title>{{ $t('group.quickActions') }}</f7-block-title>
        <div class="qa-grid grid large-grid-cols-2 xsmall-grid-cols-2 grid-gap">
          <a class="qa-card-link" :href="a.href" v-for="a in quickActions" :key="a.key">
            <f7-card class="qa-card" :style="{ background: a.color }">
              <f7-card-content>
                <div class="qa-top">
                  <f7-icon :f7="a.icon" size="20" color="white" />
                  <span v-if="a.badge" class="qa-badge">{{ a.badge }}</span>
                </div>
                <div class="qa-label">{{ a.label }}</div>
              </f7-card-content>
            </f7-card>
          </a>
        </div>

        <CtaMessageCard
          v-if="canManage && activeCampaigns.length === 0 && !loading"
          variant="prompter"
          icon="megaphone_fill"
          :title="$t('cta.homeCampaigns.title')"
          :description="$t('cta.homeCampaigns.desc')"
          :button-label="$t('cta.homeCampaigns.cta')"
          :progress="0"
          @action="goCreateCampaign"
        />

        <f7-block v-if="showMarketplaceHome" class="mkt-summary">
          <div class="mkt-summary-head">
            <div class="mkt-summary-title">
              <f7-icon f7="cart_fill" size="16" />
              <span class="margin-left-half">{{ $t('marketplace.title') }}</span>
            </div>
            <span class="mkt-summary-count">{{
              $t('marketplace.offersCount', { count: offerCount })
            }}</span>
          </div>
          <div class="mkt-summary-actions margin-top" :class="canPublish?'grid grid-cols-2 grid-gap':''">
            <f7-button fill small raised @click="goMarketplace">{{
              $t('marketplace.browse')
            }}</f7-button>
            <f7-button
              v-if="canPublish"
              outline raised
              small
              @click="goSell"
            >{{ $t('marketplace.sell') }}</f7-button>
          </div>
        </f7-block>

        <f7-block v-if="!marketplaceEnabled && canManage" class="mkt-enable">
          <div class="mkt-enable-icon"><f7-icon f7="cart_fill" size="20" /></div>
          <div class="mkt-enable-title">{{ $t('marketplace.enablePromptTitle') }}</div>
          <div class="mkt-enable-text">{{ $t('marketplace.enablePromptText') }}</div>
          <f7-button fill small @click="enableMarketplace">{{
            $t('marketplace.enableButton')
          }}</f7-button>
        </f7-block>

        <div class="section-head">
          <div class="section-head-row">
            <h2 class="section-head-title">{{ $t('ads.activeCampaigns') }}</h2>
            <f7-link
              class="section-head-link"
              :href="`/groups/${groupId}/campaigns`"
            >
              {{ $t('common.viewAll') }}
              <span class="section-head-link-icon material-symbols-outlined">chevron_right</span>
            </f7-link>
          </div>
        </div>

        <SkeletonList v-if="loading" variant="campaign" :rows="2" class="loading-block" />

        <div v-else-if="activeCampaigns.length" class="campaign-list">
          <f7-link
            v-for="c in activeCampaigns"
            :key="c.id"
            class="campaign-link"
            :href="`/groups/${groupId}/campaigns/${c.id}`"
          >
            <div class="campaign-card">
              <div class="campaign-row">
                <div
                  v-if="c.banner"
                  class="campaign-thumb"
                  :style="{ backgroundImage: `url(${c.banner})` }"
                ></div>
                <div v-else class="campaign-thumb campaign-thumb--empty">
                  <span class="campaign-thumb-icon material-symbols-outlined">{{ metaFor(c.type).icon === 'megaphone_fill' ? 'campaign' : 'volunteer_activism' }}</span>
                </div>
                <div class="campaign-main">
                  <div class="campaign-top">
                    <span class="campaign-type">
                      {{ metaFor(c.type).label }}
                    </span>
                    <span
                      v-if="c.deadline"
                      class="campaign-deadline"
                    >
                      <span class="campaign-deadline-icon material-symbols-outlined">timer</span>
                      {{ $t('grouphome.endsShort') }}
                    </span>
                  </div>
                  <h3 class="campaign-title">{{ c.name }}</h3>
                  <p v-if="c.description" class="campaign-desc">{{ c.description }}</p>
                </div>
              </div>
              <div v-if="c.targetAmount" class="campaign-progress-box">
                <div class="campaign-progress-row">
                  <div>
                    <span class="campaign-raised">{{ formatAmount(c.collectedAmount) }}</span>
                    <span class="campaign-target"> / {{ formatAmount(c.targetAmount) }}</span>
                  </div>
                  <span class="campaign-pct">{{ campaignProgressLabel(c) }}</span>
                </div>
                <div class="campaign-progress-track">
                  <div class="campaign-progress-fill" :style="{ width: campaignProgressPct(c) + '%' }"></div>
                </div>
              </div>
            </div>
          </f7-link>
        </div>

        <f7-block v-else class="campaign-empty">
          <CtaMessageCard
            variant="catalyst"
            icon="megaphone_fill"
            :title="$t('cta.campaign.title')"
            :description="$t('cta.campaign.desc')"
            :button-label="$t('cta.campaign.cta')"
            @action="goCreateCampaign"
          />
        </f7-block>

        <f7-block-title>{{ $t('common.posts') }}</f7-block-title>
        <HomeFeed :group-id="groupId" :filters="groupFeedFilters" />
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import AppLayout from '../common/AppLayout.vue'
import SkeletonList from '../common/SkeletonList.vue'
import { metaFor } from '../../constants/campaign-capabilities.js'
import { groupCategoryLabel, groupTypeLabel } from '../../constants/group-taxonomy.js'
import { useGroupsStore } from '../../js/pinia-stores/group.js'
import { useCampaignsStore } from '../../js/pinia-stores/campaign.js'
import { useGrowthStore } from '../../js/pinia-stores/growth.js'
import { useMembersStore } from '../../js/pinia-stores/members.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import { useMarketplaceStore } from '../../js/pinia-stores/marketplace.js'
import { uploadImageToR2 } from '../../js/composables/useR2Image.js'
import { trustMeta, healthMeta } from '../../constants/growth-levels.js'
import HomeFeed from '../home/HomeFeed.vue'
import CtaMessageCard from '../home/CtaMessageCard.vue'
import GroupSetupChecklist from './GroupSetupChecklist.vue'
import i18n from '../../js/i18n.js'
const { t } = i18n.global
const props = defineProps({ groupId: { type: String, required: true } })

const groupsStore = useGroupsStore()
const campaignsStore = useCampaignsStore()
const growthStore = useGrowthStore()
const membersStore = useMembersStore()
const keycloakStore = useKeycloakStore()
const marketplaceStore = useMarketplaceStore()
const router = f7.views.main.router
const group = computed(() => groupsStore.activeGroup)
const loading = ref(true)
const allCampaigns = ref([])
const bannerInput = ref(null)
const logoInput = ref(null)

const initial = computed(() => group.value?.name?.trim()?.charAt(0)?.toUpperCase() || 'G')

function navigateWithGroup(route) {
  router.navigate(route, {
    props: {
      group: group.value,
      groupId: props.groupId,
    },
  })
}

function goRecipientSetup() {
  const bt = group.value?.payoutSetup?.beneficiaryType
  navigateWithGroup(`/kyc/${bt === 'ORGANIZATION' ? 'organization' : 'individual'}/${props.groupId}`)
}

async function goPublish() {
  f7.preloader.show()
  try {
    await groupsStore.publishGroup(props.groupId)
    f7.preloader.hide()
    f7.toast.show({ text: t('groupSetup.publishGroup'), closeTimeout: 1500 })
    await groupsStore.fetchGroupDetail(props.groupId)
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || t('common.updateImageFailed'))
  }
}

const canManage = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  )
  return viewer?.role === 'OWNER' || viewer?.role === 'ADMIN'
})

const health = computed(() => growthStore.health)
const trust = computed(() => growthStore.trust)
const achievements = computed(() => growthStore.achievements)

const healthBadge = computed(() =>
  health.value.score != null ? healthMeta(health.value.score) : null)
const trustBadge = computed(() =>
  trust.value.trustLevel ? trustMeta(trust.value.trustLevel) : null)
const achievementCount = computed(() => achievements.value.length)

const hasGrowthBadges = computed(() =>
  healthBadge.value != null || trustBadge.value != null || achievementCount.value > 0)

const activeCampaigns = computed(() =>
  allCampaigns.value.filter((c) => c.status === 'ACTIVE').slice(0, 5))

const quickActions = computed(() =>
  [
    {
      key: 'newCampaign',
      label: t('group.newCampaign'),
      icon: 'megaphone_fill',
      color: '#185fa5',
      href: `/groups/${props.groupId}/campaigns/create`,
      badge: '',
    },
    {
      key: 'financialSetup',
      label: t('groupFinancialSetup.title'),
      icon: 'table',
      color: '#c0392b',
      href: `/groups/${props.groupId}/financial-setup`,
      badge: '',
      visible: canManage.value,
    },
    {
      key: 'marketplace',
      label: t('marketplace.title'),
      icon: 'cart_fill',
      color: '#1a6b42',
      href: `/groups/${props.groupId}/marketplace`,
      badge:
        canManage.value && pendingApprovals.value > 0
          ? t('marketplace.pendingApprovals', { count: pendingApprovals.value })
          : '',
      visible: showMarketplaceNav.value,
    },
    {
      key: 'saveToBuy',
      label: t('saveToBuy.quickAction'),
      icon: 'person_3_fill',
      color: '#7a3bb8',
      href: `/groups/${props.groupId}/save-to-buy`,
      badge: '',
      visible: showMarketplaceNav.value,
    },
    {
      key: 'groupBuy',
      label: t('groupBuy.quickAction'),
      icon: 'circle_grid_hex_fill',
      color: '#0e7490',
      href: `/groups/${props.groupId}/group-buy`,
      badge: '',
      visible: showMarketplaceNav.value,
    },
    {
      key: 'viewMembers',
      label: t('group.viewMembers'),
      icon: 'person_2_fill',
      color: '#0e7490',
      href: `/groups/${props.groupId}/members`,
      badge: '',
    },
    {
      key: 'importMembers',
      label: t('group.importMembers'),
      icon: 'square_arrow_down_fill',
      color: '#a08520',
      href: `/groups/${props.groupId}/members/import`,
      badge: '',
      visible: canManage.value,
    },
    {
      key: 'documents',
      label: t('common.documents'),
      icon: 'doc_text_fill',
      color: '#6b46c1',
      href: `/groups/${props.groupId}/documents`,
      badge: '',
    },
  ].filter((a) => a.visible !== false),
)

const groupFeedFilters = [
  { id: 'all', label: t('common.all') },
  { id: 'posts', label: t('common.posts') },
  { id: 'polls', label: t('poll.poll') },
]

const marketplaceSettings = computed(() => group.value?.marketplace || null)
const marketplaceEnabled = computed(() => {
  const mode = group.value?.marketplaceMode
  // Backend stores marketplace state as ModeBehavior; "OFF"/unset = disabled
  return !!mode && mode !== 'OFF'
})
const showMarketplaceNav = computed(() =>
  marketplaceEnabled.value && marketplaceSettings.value?.placement?.navigation !== false)
const showMarketplaceHome = computed(() =>
  marketplaceEnabled.value && marketplaceSettings.value?.placement?.homePage !== false)

const pendingApprovals = computed(() =>
  canManage.value ? marketplaceStore.pendingCount(props.groupId) : 0)
const offerCount = computed(() => marketplaceStore.published(props.groupId).length)

function formatAmount(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '0'
  return n.toLocaleString('fr-FR')
}
function campaignProgressPct(c) {
  const target = Number(c.targetAmount)
  const collected = Number(c.collectedAmount || c.raisedAmount || 0)
  if (!target || target <= 0) return 0
  return Math.min(100, Math.round((collected / target) * 100))
}
function campaignProgressLabel(c) {
  const pct = campaignProgressPct(c)
  if (c.status === 'ACTIVE') return `${pct}%`
  return `${pct}%`
}

const canPublish = computed(() => {
  if (!marketplaceEnabled.value) return false
  if (group.value?.marketplaceMode === 'ADMIN_ONLY') return canManage.value
  // MEMBER_OFFERS / OPEN_MARKETPLACE / FEATURED_PARTNERS: members can publish
  return true
})

function goMarketplace() {
  navigateWithGroup(`/groups/${props.groupId}/marketplace`)
}
function goSell() {
  navigateWithGroup(`/groups/${props.groupId}/marketplace/create`)
}
function goCreateCampaign() {
  navigateWithGroup(`/groups/${props.groupId}/campaigns/create`)
}

async function enableMarketplace() {
  f7.preloader.show()
  try {
    await groupsStore.updateMarketplaceMode(props.groupId, 'MEMBER_OFFERS')
    f7.preloader.hide()
    f7.toast.show({ text: t('marketplace.enableButton'), closeTimeout: 1500 })
    navigateWithGroup(`/groups/${props.groupId}/marketplace`)
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || t('marketplace.activationFailed'))
  }
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId),
    campaignsStore.fetchByGroup(props.groupId).then((c) => { allCampaigns.value = c }),
  ])
  marketplaceStore.seedMockOffers(
    props.groupId,
    keycloakStore.subject || '',
    keycloakStore.userInfo?.name || keycloakStore.userInfo?.given_name || '',
  )
  loading.value = false

  try {
    await Promise.all([
      growthStore.fetchDashboard(props.groupId),
      growthStore.fetchHealth(props.groupId),
    ])
  } catch (e) {
    // growth data unavailable — hero renders without badges
  }

  try {
    await membersStore.fetchMembers(props.groupId)
  } catch (e) {
    // members data unavailable — banner/logo edit affordance hidden
  }
})

async function onBannerPick(event) {
  const file = event.target.files?.[0]
  event.target.value = null
  await updateGroupImage('banner', file)
}

async function onLogoPick(event) {
  const file = event.target.files?.[0]
  event.target.value = null
  await updateGroupImage('logo', file)
}

async function updateGroupImage(field, file) {
  if (!file) return
  if (!file.type.startsWith('image/')) {
    f7.toast.show({ text: t('common.pleaseChooseImage'), closeTimeout: 2000 })
    return
  }
  f7.preloader.show()
  try {
    const url = await uploadImageToR2(file)
    if (!url) {
      f7.dialog.alert(t('common.updateImageFailed'))
      return
    }
    await groupsStore.updateGroup(props.groupId, { [field]: url })
    f7.toast.show({
      text: field === 'banner' ? t('common.bannerUpdated') : t('common.logoUpdated'),
      closeTimeout: 1500,
    })
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || t('common.updateImageFailed'))
  } finally {
    f7.preloader.hide()
  }
}
</script>

<style scoped>
/* ── Hero ────────────────────────────────────────────────────────── */
.group-hero {
  background-color: var(--avocash-surface-container-low) !important;
  overflow: hidden !important;
}
.hidden-file-input {
  display: none;
}
.hero-banner {
  position: relative !important;
  width: 100% !important;
  height: 176px !important;
  overflow: hidden !important;
}
.hero-banner-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}
.hero-banner-fallback {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.hero-fallback-icon {
  font-size: 40px !important;
  color: var(--f7-theme-color, #1A6B42) !important;
}
.hero-banner-overlay {
  position: absolute !important;
  inset: 0 !important;
  background-image: linear-gradient(
    to top,
    rgba(26, 107, 66, 0.9),
    rgba(26, 107, 66, 0.35),
    transparent
  ) !important;
}
.hero-badge-wrap {
  position: absolute !important;
  top: 12px !important;
  left: 12px !important;
}
.hero-verified-badge {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 4px 10px !important;
  border-radius: 9999px !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(12px) !important;
  color: var(--f7-theme-color, #1A6B42) !important;
  font-family: var(--avocash-font-body);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}
.hero-verified-icon {
  font-size: 15px !important;
  color: var(--f7-theme-color, #1A6B42) !important;
}
.hero-edit-banner {
  position: absolute !important;
  top: 12px !important;
  right: 12px !important;
  width: 36px !important;
  height: 36px !important;
  border-radius: 9999px !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(12px) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: var(--f7-theme-color, #1A6B42) !important;
}
.hero-identity {
  padding: 12px 16px 16px !important;
}
.hero-identity-row {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 12px !important;
}
.hero-identity-main {
  flex: 1 1 0% !important;
  min-width: 0 !important;
  display: flex !important;
  align-items: flex-start !important;
  gap: 12px !important;
}
.hero-logo-wrap {
  position: relative !important;
  flex-shrink: 0 !important;
}
.hero-logo {
  width: 64px !important;
  height: 64px !important;
  border-radius: 1rem !important;
  object-fit: cover !important;
  box-shadow: 0 0 0 2px #ffffff !important;
}
.hero-logo-initial {
  width: 64px !important;
  height: 64px !important;
  border-radius: 1rem !important;
  background-color: var(--f7-theme-color, #1A6B42) !important;
  color: #ffffff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-family: var(--avocash-font-headline);
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 28px;
  box-shadow: 0 0 0 2px #ffffff !important;
}
.hero-edit-logo {
  position: absolute !important;
  bottom: -4px !important;
  right: -4px !important;
  width: 24px !important;
  height: 24px !important;
  border-radius: 9999px !important;
  background-color: var(--f7-theme-color, #1A6B42) !important;
  color: #ffffff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.hero-edit-logo-icon {
  font-size: 13px !important;
}
.hero-info {
  min-width: 0 !important;
}
.hero-name {
  margin: 0;
  font-family: var(--avocash-font-headline);
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  letter-spacing: -0.025em !important;
  color: var(--f7-on-surface, #161d18) !important;
}
.hero-category {
  margin: 2px 0 0 !important;
  font-family: var(--avocash-font-body);
  font-size: 14px;
  font-weight: 500 !important;
  line-height: 20px;
  color: var(--f7-on-surface-variant, #3f4941) !important;
}
.hero-tags {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 6px !important;
  margin-top: 8px !important;
}
.hero-type-chip {
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  padding: 2px 8px !important;
  border-radius: 9999px !important;
  background-color: var(--f7-surface-container-high, #e2eae1) !important;
  color: var(--f7-on-surface-variant, #3f4941) !important;
}
.hero-growth {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 6px !important;
}
.hero-pill {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 4px 8px !important;
  border-radius: 9999px !important;
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
}
.hero-pill--tertiary {
  background-color: var(--avocash-terracotta-light, #ffdadb) !important;
  color: var(--f7-error, #772e37) !important;
}
.hero-pill-icon {
  font-size: 12px !important;
}
.hero-invite {
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 8px 14px !important;
  border-radius: 0.5rem !important;
  background-color: var(--f7-theme-color, #1A6B42) !important;
  color: #ffffff !important;
  font-family: var(--avocash-font-body);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}
.hero-invite-icon {
  font-size: 16px !important;
}

/* ── At a glance ribbon ──────────────────────────────────────────── */
.glance-section {
  padding-left: 16px !important;
  padding-right: 16px !important;
  margin-top: 12px !important;
}
.glance-grid {
  gap: 8px !important;
}
.glance-card {
  color: inherit !important;
  text-decoration: none !important;
}
.glance-card :deep(.link) {
  color: inherit;
}
.glance-card-inner {
  background-color: var(--f7-card-bg-color, #ffffff) !important;
  padding: 12px !important;
  border-radius: 0.5rem !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: space-between !important;
}
.glance-card-top {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
}
.glance-icon {
  font-size: 20px !important;
}
.glance-icon--primary {
  color: var(--f7-theme-color, #1A6B42) !important;
}
.glance-icon--secondary {
  color: var(--f7-secondary, #755b00) !important;
}
.glance-icon--tertiary {
  color: var(--f7-error, #772e37) !important;
}
.glance-badge {
  padding: 2px 6px !important;
  border-radius: 0.25rem !important;
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 700 !important;
  line-height: 14px;
}
.glance-badge--primary {
  background-color: #a5f4bf !important;
  color: #0e3722 !important;
}
.glance-badge--secondary {
  background-color: #ffe08f !important;
  color: #2d2411 !important;
}
.glance-card-body {
  margin-top: 8px !important;
}
.glance-card-value {
  font-family: var(--avocash-font-headline);
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 28px;
  color: var(--f7-on-surface, #161d18) !important;
}
.glance-card-label {
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.glance-dot {
  width: 8px !important;
  height: 8px !important;
  border-radius: 9999px !important;
  background-color: var(--f7-error, #772e37) !important;
}

/* ── Quick actions ───────────────────────────────────────────────── */
.qa-grid {
  margin: 0 16px !important;
}
.qa-card-link {
  display: block !important;
  color: inherit !important;
}
.qa-card {
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14) !important;
  margin: 0 !important;
}
.qa-card :deep(.card-content) {
  padding: 12px;
}
.qa-top {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 8px !important;
}
.qa-label {
  margin-top: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #fff !important;
  line-height: 1.3 !important;
}
.qa-badge {
  font-size: 9px !important;
  font-weight: 700 !important;
  padding: 2px 6px !important;
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  color: #333 !important;
  white-space: nowrap !important;
}

/* ── Active campaigns header ─────────────────────────────────────── */
.section-head {
  padding: 0 16px !important;
  margin-top: 20px !important;
}
.section-head-row {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-bottom: 12px !important;
}
.section-head-title {
  margin: 0;
  font-family: var(--avocash-font-headline);
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 28px;
  color: var(--f7-on-surface, #161d18) !important;
}
.section-head-link {
  display: flex !important;
  align-items: center !important;
  gap: 2px !important;
  font-family: var(--avocash-font-body);
  font-size: 12px;
  font-weight: 600 !important;
  line-height: 16px;
  color: var(--f7-theme-color, #1A6B42) !important;
  text-decoration: none !important;
}
.section-head-link-icon {
  font-size: 16px !important;
}

/* ── Campaign list ───────────────────────────────────────────────── */
.loading-block {
  text-align: center;
  padding: 20px 0;
}
.campaign-list {
  padding: 0 16px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 12px !important;
}
.campaign-link {
  text-decoration: none !important;
}
.campaign-card {
  background-color: var(--f7-card-bg-color, #ffffff) !important;
  border-radius: 0.75rem !important;
  padding: 16px !important;
}
.campaign-row {
  display: flex !important;
  gap: 12px !important;
}
.campaign-thumb {
  width: 64px !important;
  height: 64px !important;
  border-radius: 0.5rem !important;
  overflow: hidden !important;
  flex-shrink: 0 !important;
  background-size: cover !important;
  background-position: center !important;
}
.campaign-thumb--empty {
  background-color: rgba(165, 244, 191, 0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.campaign-thumb-icon {
  font-size: 26px !important;
  color: var(--f7-theme-color, #1A6B42) !important;
}
.campaign-main {
  flex: 1 1 0% !important;
  min-width: 0 !important;
}
.campaign-top {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 4px !important;
}
.campaign-type {
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 600 !important;
  line-height: 14px;
  padding: 2px 8px !important;
  border-radius: 9999px !important;
  background-color: #a5f4bf !important;
  color: #0e3722 !important;
}
.campaign-deadline {
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 600 !important;
  line-height: 14px;
  color: var(--f7-error, #772e37) !important;
}
.campaign-deadline-icon {
  font-size: 14px !important;
}
.campaign-title {
  margin: 4px 0 0 !important;
  font-family: var(--avocash-font-headline);
  font-size: 16px;
  font-weight: 700 !important;
  line-height: 1.375 !important;
  color: var(--f7-on-surface, #161d18) !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.campaign-desc {
  margin: 2px 0 0 !important;
  font-family: var(--avocash-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 1 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
}
.campaign-progress-box {
  background-color: var(--avocash-surface-container-low) !important;
  padding: 12px !important;
  border-radius: 0.5rem !important;
}
.campaign-progress-row {
  display: flex !important;
  align-items: baseline !important;
  justify-content: space-between !important;
}
.campaign-raised {
  font-family: var(--avocash-font-headline);
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 28px;
  color: var(--f7-theme-color, #1A6B42) !important;
}
.campaign-target {
  font-family: var(--avocash-font-body);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
}
.campaign-pct {
  font-family: var(--avocash-font-body);
  font-size: 12px;
  font-weight: 600 !important;
  line-height: 16px;
  color: var(--f7-on-surface, #161d18) !important;
}
.campaign-progress-track {
  width: 100% !important;
  height: 8px !important;
  background-color: var(--f7-surface-container-high, #e2eae1) !important;
  border-radius: 9999px !important;
  overflow: hidden !important;
  margin-top: 8px !important;
}
.campaign-progress-fill {
  height: 100% !important;
  background-color: var(--f7-theme-color, #1A6B42) !important;
  border-radius: 9999px !important;
}
.campaign-empty {
  padding-left: 16px !important;
  padding-right: 16px !important;
}
</style>
